/*
 * <featured-tabs> — tab switcher for sections/custom-featured-tabs.liquid.
 *
 * All tab panels are server-rendered; this component just toggles which one is
 * visible. Implements the WAI-ARIA "Tabs with Automatic Activation" pattern
 * (Left/Right/Up/Down/Home/End move and activate). The product cards inside the
 * panels keep their own behaviour from assets/collection-card.js — this file
 * never touches them.
 */
if (!customElements.get('featured-tabs')) {
  customElements.define(
    'featured-tabs',
    class FeaturedTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        if (this.tabs.length === 0) return;

        // Per-panel horizontal scroll memory: every tab's slider keeps its own
        // scroll position across tab switches (see activateTab). A panel that was
        // never scrolled simply has no entry → restored to 0 (its first card).
        this.scrollPositions = new WeakMap();

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.activateTab(tab));
          tab.addEventListener('keydown', (event) => this.onKeydown(event));
        });

        // Theme editor: activate the tab whose block the merchant selects.
        this.addEventListener('shopify:block:select', (event) => {
          const tab = event.target.closest('[role="tab"]');
          if (tab) this.activateTab(tab, { focus: false });
        });

        // On a fresh load (or a back/forward bfcache restore) the browser restores
        // the slider's previous horizontal scroll position. Our in-memory WeakMap is
        // empty after a refresh, and the initially-visible panel never passes through
        // activateTab, so force it back to its first card so a refresh starts clean.
        // `pageshow` covers both the normal load and the bfcache case (mobile Safari).
        this.resetVisiblePanelScroll();
        window.addEventListener('pageshow', () => this.resetVisiblePanelScroll());

        // Equal-height cards across every tab. The shared card grows with its
        // optional content (artist subtitle, preorder ship date, 1- vs 2-line
        // title, wrapped price), so a tab whose products have less of it renders a
        // shorter panel — switching tabs then changes the section height. Measure
        // the tallest card across ALL tabs and pin them all to it. Re-run after web
        // fonts load (text metrics change the height) and on resize (card width —
        // and thus the cqi-scaled type — changes).
        this.equalizeCardHeights();
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.equalizeCardHeights());
        }
        let resizeTimer = null;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.equalizeCardHeights(), 150);
        });
      }

      // Pin every product card in the section to the height of the tallest one,
      // measured across all tabs (not just the visible panel).
      equalizeCardHeights() {
        if (!this.panels) return;
        const cards = Array.from(this.querySelectorAll('.collection-card'));
        if (cards.length < 2) return;

        // Reveal every panel so cards in inactive tabs report real layout. The
        // whole measure pass is synchronous — no await / rAF between reveal and
        // restore — so the browser paints only the final state and the hidden
        // panels never flash on screen.
        const hiddenPanels = this.panels.filter((panel) => panel.hasAttribute('hidden'));
        hiddenPanels.forEach((panel) => panel.removeAttribute('hidden'));

        // Drop the previous pin so each card collapses to its natural height first.
        cards.forEach((card) => {
          card.style.minHeight = '';
        });

        // Reading offsetHeight forces the one synchronous reflow we need.
        let max = 0;
        cards.forEach((card) => {
          if (card.offsetHeight > max) max = card.offsetHeight;
        });

        // Re-hide the inactive panels before any paint, then pin all cards.
        hiddenPanels.forEach((panel) => panel.setAttribute('hidden', ''));
        if (max > 0) {
          cards.forEach((card) => {
            card.style.minHeight = `${max}px`;
          });
        }
      }

      // The horizontal scroll-snap track inside a panel.
      getScroller(panel) {
        return panel && panel.querySelector('.featured-tabs__grid, [id^="Slider-"]');
      }

      // Snap the currently-visible panel's slider back to its first card, instantly.
      resetVisiblePanelScroll() {
        const panel = this.panels.find((current) => !current.hasAttribute('hidden'));
        const scroller = this.getScroller(panel);
        if (!scroller) return;
        requestAnimationFrame(() => {
          if (scroller.scrollLeft === 0) return;
          const prevBehavior = scroller.style.scrollBehavior;
          scroller.style.scrollBehavior = 'auto';
          scroller.scrollLeft = 0;
          scroller.style.scrollBehavior = prevBehavior;
        });
      }

      activateTab(tab, { focus = true } = {}) {
        const index = this.tabs.indexOf(tab);
        if (index === -1) return;

        // Remember where the customer left the panel we're leaving, so we can put it
        // back exactly there when they return. Read it now, while it's still visible
        // (scrollLeft reads 0 on a display:none element).
        const leavingPanel = this.panels.find((panel) => !panel.hasAttribute('hidden'));
        if (leavingPanel) {
          const leavingScroller = this.getScroller(leavingPanel);
          if (leavingScroller) this.scrollPositions.set(leavingPanel, leavingScroller.scrollLeft);
        }

        this.tabs.forEach((current, i) => {
          const selected = i === index;
          current.classList.toggle('is-active', selected);
          current.setAttribute('aria-selected', selected ? 'true' : 'false');
          current.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.panels.forEach((panel, i) => {
          const selected = i === index;
          panel.classList.toggle('is-active', selected);
          panel.toggleAttribute('hidden', !selected);
        });

        // Each panel's grid is a horizontal scroll-snap slider on mobile. Two things
        // fight us when a [hidden] panel is revealed: iOS Safari re-applies
        // scroll-snap and lands the track on an arbitrary card, and the slider has
        // `scroll-behavior: smooth` (component-slider.css) which would animate any
        // correction. So restore the panel's *remembered* position (0 if it was
        // never scrolled) instantly — no visible movement, and a tab the customer
        // had scrolled re-opens exactly where they left it. rAF defers until the
        // un-hide above has taken layout effect (scrollLeft no-ops on display:none).
        const activePanel = this.panels[index];
        if (activePanel) {
          const saved = this.scrollPositions.get(activePanel) || 0;
          requestAnimationFrame(() => {
            const scroller = this.getScroller(activePanel);
            if (!scroller || scroller.scrollLeft === saved) return;
            const prevBehavior = scroller.style.scrollBehavior;
            scroller.style.scrollBehavior = 'auto';
            scroller.scrollLeft = saved;
            scroller.style.scrollBehavior = prevBehavior;
          });
        }

        if (focus) tab.focus();
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.target);
        if (index === -1) return;

        let nextIndex = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            nextIndex = (index + 1) % this.tabs.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.activateTab(this.tabs[nextIndex]);
      }
    }
  );
}

/*
 * Dots paginator for the mobile swipe slider (per merchant request: dots only, no
 * arrows, swipe-driven). One controller per tab panel's `.featured-tabs__slider`.
 *
 * Movement is 100% native CSS scroll-snap — this JS only paints the dots and
 * reflects/controls scroll position. A ResizeObserver re-measures when a hidden
 * tab panel becomes visible (its items report 0 layout while [hidden]).
 *
 * Dots map to *reachable scroll positions (pages)*, not one-per-card. With the
 * ~2.2-card peek the last ~1.2 cards can never scroll to the left edge — their
 * left-aligned positions all clamp onto the same end position. One-dot-per-card
 * therefore produced a phantom second-to-last dot that shared the end position
 * with the last dot and got skipped instantly. Building one dot per *distinct*
 * reachable position removes that artifact: every dot lands on its own spot.
 */
(function setupFeaturedTabsDots() {
  function initDots(container) {
    const sliderId = container.getAttribute('data-slider');
    const slider = sliderId && document.getElementById(sliderId);
    if (!slider) return;

    const items = Array.from(slider.querySelectorAll('[id^="Slide-"]'));
    if (items.length < 2) return;

    let dots = [];

    // The set of distinct scroll-left positions the track can actually rest at:
    // each card's left-aligned offset, clamped to [0, maxScroll] and de-duped so
    // the trailing cards that collapse onto the end share a single page. Returns
    // null while the panel is hidden / non-scrollable (nothing to measure).
    const getPositions = () => {
      const base = items[0].offsetLeft;
      if (items[1].offsetLeft === base) return null; // hidden panel: no layout
      const max = slider.scrollWidth - slider.clientWidth;
      if (max <= 1) return null; // everything fits: no pagination needed
      const positions = [];
      items.forEach((item) => {
        const pos = Math.min(Math.max(item.offsetLeft - base, 0), max);
        if (!positions.length || pos - positions[positions.length - 1] > 1) {
          positions.push(pos);
        }
      });
      return positions;
    };

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    };

    const buildDots = (count) => {
      container.textContent = '';
      dots = [];
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'featured-tabs__dot';
        dot.setAttribute('aria-label', `Go to page ${i + 1}`);
        dot.addEventListener('click', () => {
          const positions = getPositions();
          if (!positions) return;
          const target = positions[Math.min(i, positions.length - 1)];
          slider.scrollTo({ left: target, behavior: 'smooth' });
        });
        container.appendChild(dot);
        dots.push(dot);
      }
    };

    const update = () => {
      const positions = getPositions();
      if (!positions) return;
      if (positions.length !== dots.length) buildDots(positions.length);
      // Active = the page whose rest-position is nearest the current scrollLeft.
      let nearest = 0;
      let best = Infinity;
      positions.forEach((pos, i) => {
        const dist = Math.abs(slider.scrollLeft - pos);
        if (dist < best) {
          best = dist;
          nearest = i;
        }
      });
      setActive(nearest);
    };

    let ticking = false;
    slider.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          update();
        });
      },
      { passive: true }
    );

    // Re-measure when the panel resizes (incl. hidden → visible on tab switch).
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => update()).observe(slider);
    }

    update();
  }

  function run() {
    document.querySelectorAll('[data-featured-dots]').forEach(initDots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
